import { useCallback, useState } from 'react';
import { css } from '@emotion/react';
import { HttpError } from '@frontend/fetch';
import { theme } from '@frontend/theme';
import { Text, useAlert } from '@frontend/design-system';
import { useCareCreditTranslations } from '../../../hooks/use-care-credit-translations';
import { EnrollmentBaseStep } from '../atoms/step/enrollment-base-step';
import { EnrollmentStepPerformanceWrapper } from '../atoms/step/enrollment-step-performance-wrapper';
import { CareCreditEnrollmentForm } from '../components/care-credit-enrollment-form';
import { useCareCreditEnrollmentContext } from '../context/care-credit-enrollment.context';
import { CareCreditEnrollmentMultiSteps, EnrollmentFormFieldNames } from '../enrollment-multi-step.types';
import {
  getQuickOfferResponseErrorDetails,
  QuickOfferFieldError,
  quickOfferResponseIsDeclined,
  quickOfferResponseIsSuccess,
} from '../utils/quick-offer';

const CURRENT_STEP: CareCreditEnrollmentMultiSteps = 'new-application-form';
const PREV_STEP: CareCreditEnrollmentMultiSteps = 'select-process-method';
const NEXT_STEP: CareCreditEnrollmentMultiSteps = 'applicant-pre-qualified-confirm';

const styles = {
  content: css`
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing(2)};
  `,
};

export const Step2ApplicationForm = () => {
  return (
    <EnrollmentStepPerformanceWrapper step={CURRENT_STEP}>
      <Step2ApplicationFormBody />
    </EnrollmentStepPerformanceWrapper>
  );
};

const Step2ApplicationFormBody = () => {
  const { t } = useCareCreditTranslations();
  const alerts = useAlert();

  const [errorState, setErrorState] = useState<{ fields: QuickOfferFieldError[]; message: string } | null>(null);

  const {
    invoice,
    multiStep: { goNextStep, goPrevStep },
    enrollmentFormData,
    onClearIdFields,
    onSubmitApplication,
    onChangeEnrollmentFormField,
    onChangeEnrollmentFormComplete,
    onEnrollmentDeclined,
    isSubmitting,
  } = useCareCreditEnrollmentContext();

  const [submitAttempts, setSubmitAttempts] = useState(0);

  const onClickNext = useCallback(async () => {
    setErrorState(null);
    setSubmitAttempts((attempts) => attempts + 1);

    if (!enrollmentFormData.isValid) {
      return;
    }

    try {
      const result = await onSubmitApplication(enrollmentFormData);

      if (quickOfferResponseIsSuccess(result)) {
        goNextStep({ orStep: NEXT_STEP });
      } else {
        const error = getQuickOfferResponseErrorDetails(result);

        if (quickOfferResponseIsDeclined(result)) {
          onEnrollmentDeclined({
            status: 'declined',
            acceptPayload: null,
            statusPayload: null,
            message:
              error?.errorMessage ??
              t('Your application was declined. Please review the information you entered and try again.'),
          });
        } else {
          if (error) {
            const { errorCode, errorMessage, fieldErrors } = error;

            if (errorCode === 'gateway.validation.failure') {
              alerts.error(t('Invalid information provided.'));
            } else {
              alerts.error(t('Submission error. Please review the form and try again.'));
            }

            setErrorState({
              message: errorMessage,
              fields: fieldErrors,
            });
          } else {
            alerts.error(t('An unknown error occurred. Please try again.'));
          }
        }
      }
    } catch (err) {
      console.error('Caught error submitting CareCredit application', err);
      if (err instanceof HttpError) {
        // if applicationError.message is json, parse it, otherwise use message.
        try {
          const errorData = JSON.parse(err.message);

          let message = errorData.message
            ? `${errorData.message}. Please try again.`
            : 'An error occurred while submitting your application. Please try again.';
          if (errorData?.code === 3) {
            message = 'Submission validation failed. Please review the form and try again.';
          }

          const fields: QuickOfferFieldError[] = [];

          if (errorData?.details?.[0]) {
            const details = errorData.details[0];
            if (details?.fieldViolations) {
              (details.fieldViolations as { field: string; description: string }[]).forEach((violation) => {
                fields.push({
                  field: violation.field as EnrollmentFormFieldNames,
                  message: violation.description,
                });
              });
            }
          }

          setErrorState({
            message,
            fields,
          });
        } catch (err2) {
          if (err2 instanceof Error) {
            setErrorState({
              message: err2.message || 'An error occurred while submitting your application. Please try again.',
              fields: [],
            });
          }
        }
      }
    }
  }, [onSubmitApplication, enrollmentFormData, goNextStep]);

  const onClickBack = useCallback(() => {
    goPrevStep({ orStep: PREV_STEP });
  }, [goPrevStep]);

  return (
    <EnrollmentBaseStep
      id={CURRENT_STEP}
      label={t('New Prequalification')}
      footer={{
        primary: {
          children: t('Check Prequalification'),
          disabled: /* !enrollmentFormData.isValid */ isSubmitting,
          loading: isSubmitting,
        },
        onClickPrimary: onClickNext,
        back: {
          children: t('Back'),
          iconName: 'back',
        },
        showBackButton: true,
        onClickBack,
      }}
    >
      <div css={styles.content}>
        <Text>
          {t(
            'The application will start with a prequalification, submitted at the applicant’s request. Prequalifying only requires a “soft” inquiry on the applicant’s credit report and has no impact on their credit bureau score.'
          )}
        </Text>

        <CareCreditEnrollmentForm
          invoice={invoice}
          key={enrollmentFormData.fields.idType} // reset form when id type changes, since that can change required fields
          formState={enrollmentFormData}
          onClearIdFields={onClearIdFields}
          onChangeField={onChangeEnrollmentFormField}
          onChangeComplete={onChangeEnrollmentFormComplete}
          errorState={errorState}
          submitAttempts={submitAttempts}
        />
      </div>
    </EnrollmentBaseStep>
  );
};
